
import { Component, Emit, Inject, Vue } from "vue-property-decorator";
import Modal from "../../../../common/src/components/Modal.vue";
import { Container } from "inversify";
import ActionButton from "../../../../common/src/components/ActionButton.vue";
import ConferenceForm from "./ConferenceForm.vue";
import { ConferenceEditStore } from "../../stores/conference/ConferenceEditStore";
import { AuthStore } from "../../../../common/src/stores/AuthStore";
import { ConferenceDestroyStore } from "../../stores/conference/ConferenceDestroyStore";
import { UserEditFavoriteStore } from "../../stores/user/UserEditFavoriteStore";
import FavoriteBtn from "../components/FavoriteBtn.vue";
import { toLocaleDateTimeString } from "@gsx/common";
import { OrganizationListStore } from "../../stores/organization/OrganizationListStore";
import { ConferenceListStore } from "../../stores/conference/ConferenceListStore";
import exportToCalendar from "../../utils/downloadConferenceToCalendar";

@Component({
    components: { ConferenceForm, ActionButton, Modal, FavoriteBtn },
})
export default class ViewEventModal extends Vue {
    @Inject("container") readonly container!: Container;
    authStore: AuthStore = this.container.get(AuthStore);
    editStore: ConferenceEditStore = this.container.get(ConferenceEditStore);
    destroyStore: ConferenceDestroyStore = this.container.get(ConferenceDestroyStore);
    userEditFavoriteStore: UserEditFavoriteStore = this.container.get(UserEditFavoriteStore);
    readonly organizationListStore: OrganizationListStore =
        this.container.get(OrganizationListStore);
    readonly conferenceListStore: ConferenceListStore = this.container.get(ConferenceListStore);

    mounted() {
        // mandatory for Export to calendar
        const plugin = document.createElement("script");
        plugin.setAttribute(
            "src",
            "https://cdn.jsdelivr.net/gh/nwcell/ics.js@0.2.0/ics.deps.min.js",
        );
        plugin.async = true;
        document.head.appendChild(plugin);
    }

    get startDate(): string {
        return toLocaleDateTimeString(this.values.startDate) || "n/a";
    }

    get endDate(): string {
        return toLocaleDateTimeString(this.values.endDate) || "";
    }

    get values() {
        return this.editStore.data;
    }

    get formattedDeadlines() {
        return (
            this.values.deadlines
                ?.sort((a, b) => (b.date.getTime() > a.date.getTime() ? -1 : 1))
                .map((deadline) => ({
                    ...deadline,
                    date: toLocaleDateTimeString(deadline.date),
                })) || []
        );
    }

    get isConferenceFavorite() {
        return this.authStore.user?.favorites?.conferenceIds?.includes(
            this.editStore.conference!.conferenceid,
        );
    }

    get conferenceOrganizingOrganizations() {
        return this.values.organizingOrganizations;
    }

    get similarEvents() {
        return this.conferenceListStore
            .items!.filter((event) => {
                let eventCountMatches =
                    (event.topics?.filter((topic) => this.values.topics?.includes(topic)).length ||
                        0) +
                    (event.types?.filter((type) => this.values?.types?.includes(type)).length || 0);
                return event.name !== this.values.name && eventCountMatches > 0;
            })
            .sort((a, b) => {
                let countMatchesA =
                    (a.topics?.filter((topic) => this.values.topics?.includes(topic)).length || 0) +
                    (a.types?.filter((type) => this.values?.types?.includes(type)).length || 0);
                let countMatchesB =
                    (b.topics?.filter((topic) => this.values.topics?.includes(topic)).length || 0) +
                    (b.types?.filter((type) => this.values?.types?.includes(type)).length || 0);
                return countMatchesB > countMatchesA
                    ? 1
                    : countMatchesB === countMatchesA
                    ? b.startDate.getTime() < a.startDate.getTime()
                        ? 1
                        : -1
                    : -1;
            })
            .map((event) => ({
                ...event,
                endDate: toLocaleDateTimeString(event.endDate) || "",
                startDate: toLocaleDateTimeString(event.startDate) || "",
            }))
            .slice(0, 3);
    }

    toggleFavorites() {
        const conferenceId = this.editStore.conference!.conferenceid;
        if (this.isConferenceFavorite) {
            const indexOf =
                this.authStore.user!.favorites!.conferenceIds?.indexOf(conferenceId) || -1;
            if (indexOf >= 0) {
                this.authStore.user?.favorites?.conferenceIds?.splice(indexOf, 1);
            }
            this.userEditFavoriteStore.removeConference(conferenceId);
        } else {
            this.authStore.user?.favorites?.conferenceIds?.push(conferenceId);
            this.userEditFavoriteStore.addConference(conferenceId);
        }
    }

    navigateToOrg(organizationName: string) {
        const org = this.organizationListStore.items?.find(
            (org) => org.organizationName === organizationName,
        );

        if (org) {
            this.$router.push("/organizations/" + org.organizationid);
            this.close();
        }
    }

    openEvent(id: string) {
        const newEvent = this.conferenceListStore.items!.find((event) => event.conferenceid === id);
        if (newEvent) {
            this.editStore.setConference(newEvent);
        }
    }

    downloadEventToCalendar() {
        exportToCalendar(this.editStore.conference!);
    }

    @Emit()
    close() {
        //
    }
}
